import { useEffect } from "react";
import { useSettingsStore } from "@/store/api/settingsStore";
import { useTheme } from "@/components/theme/ThemeProvider";
import { useAppName } from "@/hooks/useAppName";
import { resolveAppBranding } from "@/config/appBranding";
import { applyThemeMode, athleteThemeStyle, hexToHsl, resolveAthleteTheme, resolveThemePolicy } from "@/lib/athleteTheme";

// La table des marques vit désormais dans src/config/appBranding.ts, partagée
// avec la construction des liens de réinitialisation (hôte dédié par app).


/**
 * Hook to manage theme customization based on gym settings
 */
export const useThemeCustomization = (forcedAppName: string = null) => {
  const { settings } = useSettingsStore();
  const { theme } = useTheme();
  const detectedAppName = useAppName(); // Always call hook unconditionally
  const appName = forcedAppName ?? settings?.appName ?? detectedAppName;

  // Apply dynamic primary color to CSS variables
  useEffect(() => {
    const root = document.documentElement;
    const primaryColor = settings?.appearance?.primaryColor || "#F59E0B";
    
    try {
      const hsl = hexToHsl(primaryColor);
      root.style.setProperty('--primary', hsl);
      root.style.setProperty('--accent', hsl);
      root.style.setProperty('--sidebar-primary', hsl);
    } catch (error) {
      console.error("Error converting color:", error);
      // Fallback to default amber
      root.style.setProperty('--primary', '38 92% 50%');
      root.style.setProperty('--accent', '38 92% 50%');
      root.style.setProperty('--sidebar-primary', '38 92% 50%');
    }
  }, [settings?.appearance?.primaryColor]);
  
  const apparenceFromTransco = resolveAppBranding(appName);
  const themePolicy = resolveThemePolicy(settings?.appearance?.themePolicy, apparenceFromTransco.key === "VB20TN");
  const configuredAthleteTheme = resolveAthleteTheme(
    settings?.appearance?.athleteTheme,
    apparenceFromTransco.key === "VB20TN",
    settings?.appearance?.primaryColor,
  );

  // Get appropriate logo based on theme
  const effectiveTheme = themePolicy === "user-choice" ? theme : themePolicy;
  const athleteTheme = applyThemeMode(configuredAthleteTheme, effectiveTheme);
  const logoUrl = effectiveTheme === "light" 
    ? (settings?.appearance?.logoLightUrl || apparenceFromTransco.logoLightUrl)
    : (settings?.appearance?.logoDarkUrl || apparenceFromTransco.logoDarkLightUrl);
  
  return {
    appKey: apparenceFromTransco.key,
    themePolicy,
    canChooseTheme: themePolicy === "user-choice",
    logoLight: settings?.appearance?.logoLightUrl || apparenceFromTransco.logoLightUrl,
    logoDark: settings?.appearance?.logoDarkUrl || apparenceFromTransco.logoDarkLightUrl,
    logoUrl,
    brandName: settings?.appearance?.brandName || apparenceFromTransco.longName,
    primaryColor: settings?.appearance?.primaryColor || "#F59E0B",
    athleteTheme,
    athleteThemeStyle: athleteThemeStyle(athleteTheme),
    athleteThemeClass: athleteTheme.variant === "dark-premium" ? "athlete-shell--vb20" : "athlete-shell--standard",
    scheduleDensity: athleteTheme.scheduleDensity,
    athleteBackgrounds: athleteTheme.backgrounds,
    appleStoreLink: apparenceFromTransco.appleStoreLink,
    playStoreLink: apparenceFromTransco.playStoreLink,
    deepLinkShortcut: apparenceFromTransco.deepLinkShortcut
  };
};
